/**
 * Fuseau horaire, cote serveur comme cote client.
 *
 * Ces fonctions vivent dans le paquet partage pour une raison precise : le
 * Worker decide a 6 h 30 de ce qui est « du aujourd'hui », et le front
 * reaffiche la meme chose a 6 h 31. S'ils calculaient chacun leur date, un
 * rendez-vous de 23 h 30 pourrait apparaitre dans le brief du serveur et pas
 * dans celui de l'ecran, ou l'inverse. Un seul calcul, deux appels.
 *
 * Tout passe par Intl : les Workers embarquent les donnees ICU completes, et
 * les navigateurs aussi. Aucun decalage n'est jamais code en dur, ce qui rend
 * le passage heure avancee / heure normale transparent.
 */

const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(cle: string, locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let f = cache.get(cle);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    cache.set(cle, f);
  }
  return f;
}

/** Une echeance sans heure, ex. « 2026-09-19 ». */
export const MOTIF_DATE_SEULE = /^\d{4}-\d{2}-\d{2}$/;

/** Date du jour AAAA-MM-JJ telle que vue dans `fuseau`. */
export function jourLocal(instant: Date, fuseau: string): string {
  // en-CA produit nativement le format AAAA-MM-JJ, directement comparable
  // avec un <, ce qui evite de manipuler des objets Date pour trier.
  return fmt('jour:' + fuseau, 'en-CA', {
    timeZone: fuseau,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** Heure locale en horloge 24 h. */
export function heureLocale(instant: Date, fuseau: string): { heure: number; minute: number } {
  const parts = fmt('hm:' + fuseau, 'en-CA', {
    timeZone: fuseau,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const lire = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return { heure: lire('hour'), minute: lire('minute') };
}

/** Vrai si l'echeance porte une heure, et pas seulement une date. */
export function aUneHeure(du_le: string | null): boolean {
  return du_le !== null && !MOTIF_DATE_SEULE.test(du_le);
}

/**
 * Jour local d'une echeance.
 *
 * Une date seule est rendue telle quelle : la reinterpreter comme un instant
 * la ferait glisser d'un jour selon le fuseau. Un instant complet, lui, est
 * bien projete dans le fuseau de la famille.
 */
export function jourDeEcheance(du_le: string, fuseau: string): string | null {
  if (MOTIF_DATE_SEULE.test(du_le)) return du_le;
  const d = new Date(du_le);
  if (Number.isNaN(d.getTime())) return null;
  return jourLocal(d, fuseau);
}

/** « vendredi 19 septembre ». */
export function libelleDate(instant: Date, fuseau: string): string {
  return fmt('libelle:' + fuseau, 'fr-CA', {
    timeZone: fuseau,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(instant);
}

/** « 09 h 00 », l'heure d'un rendez-vous. */
export function heureAffichee(iso: string, fuseau: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return fmt('hhmm:' + fuseau, 'fr-CA', {
    timeZone: fuseau,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
}

/** « 19 sept. », pour une echeance passee. */
export function jourCourt(du_le: string, fuseau: string): string {
  const d = MOTIF_DATE_SEULE.test(du_le) ? new Date(du_le + 'T12:00:00Z') : new Date(du_le);
  if (Number.isNaN(d.getTime())) return '';
  return fmt('court:' + fuseau, 'fr-CA', {
    timeZone: fuseau,
    day: 'numeric',
    month: 'short',
  }).format(d);
}

export function majuscule(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/**
 * Decalage UTC de `fuseau` a cet instant, en minutes.
 *
 * On n'utilise pas `timeZoneName: 'longOffset'`, apparu tard dans Safari :
 * on formate l'instant dans le fuseau, on relit les champs comme s'ils
 * etaient UTC, et on mesure l'ecart. Marche partout, sans table de fuseaux.
 */
export function decalageMinutes(instant: Date, fuseau: string): number {
  const parts = fmt('off:' + fuseau, 'en-US', {
    timeZone: fuseau,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const lire = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const commeUtc = Date.UTC(
    lire('year'),
    lire('month') - 1,
    lire('day'),
    lire('hour'),
    lire('minute'),
    lire('second'),
  );
  return Math.round((commeUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Combine une date (AAAA-MM-JJ) et une heure (HH:MM) lues a l'ecran en un
 * instant ISO8601 portant le bon decalage.
 *
 * L'heure saisie est une heure murale : « 14:30 le 2 novembre » doit rester
 * 14 h 30 a Montreal, que ce jour-la soit en heure avancee ou normale. On
 * part d'une estimation, on mesure le decalage reel a cette estimation, on
 * corrige, et on recommence une fois — la seconde passe ne sert que pour les
 * deux jours de bascule, ou la premiere estimation peut tomber du mauvais
 * cote du changement d'heure.
 */
export function isoDepuisDateEtHeure(date: string, heure: string, fuseau: string): string {
  const [a, m, j] = date.split('-').map(Number);
  const [hh, mm] = heure.split(':').map(Number);
  const mur = Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1, hh ?? 0, mm ?? 0);

  let ts = mur;
  for (let i = 0; i < 2; i++) {
    ts = mur - decalageMinutes(new Date(ts), fuseau) * 60000;
  }

  const off = decalageMinutes(new Date(ts), fuseau);
  const signe = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return (
    date + 'T' + pad2(hh ?? 0) + ':' + pad2(mm ?? 0) + ':00' + signe + pad2(Math.floor(abs / 60)) + ':' + pad2(abs % 60)
  );
}

/** Decompose une echeance pour pre-remplir les champs date et heure. */
export function champsDepuisEcheance(
  du_le: string | null,
  fuseau: string,
): { date: string; heure: string } {
  if (!du_le) return { date: '', heure: '' };
  if (MOTIF_DATE_SEULE.test(du_le)) return { date: du_le, heure: '' };
  const d = new Date(du_le);
  if (Number.isNaN(d.getTime())) return { date: '', heure: '' };
  const parts = fmt('champs:' + fuseau, 'en-CA', {
    timeZone: fuseau,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const v = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { date: v('year') + '-' + v('month') + '-' + v('day'), heure: v('hour') + ':' + v('minute') };
}

/** Minutes avant l'echeance auxquelles un rappel est envoye. */
export const MINUTES_RAPPEL = [15, 5] as const;

/**
 * Instants de rappel d'un item, en millisecondes. Vide si l'item n'a pas
 * d'heure precise ou n'est plus a faire : on ne rappelle pas une tache
 * cochee, ni une tache « dans la journee » qui n'a pas d'heure a annoncer.
 */
export function instantsDeRappel(item: {
  statut: string;
  du_le: string | null;
}): { minutes: number; instant: number }[] {
  if (item.statut !== 'todo') return [];
  if (!item.du_le || !aUneHeure(item.du_le)) return [];
  const echeance = new Date(item.du_le).getTime();
  if (Number.isNaN(echeance)) return [];
  return MINUTES_RAPPEL.map((minutes) => ({ minutes, instant: echeance - minutes * 60000 }));
}

/**
 * Recurrences gerees. Sous-ensemble volontaire de la RFC 5545 : la colonne
 * `recurrence` stocke une vraie RRULE, donc rien n'empeche d'en gerer
 * davantage plus tard sans migration, mais interpreter BYDAY, BYSETPOS et les
 * exceptions demanderait une bibliotheque entiere pour un besoin qui tient en
 * quatre cas.
 */
export const RECURRENCES = [
  { valeur: null, libelle: 'Jamais' },
  { valeur: 'FREQ=DAILY', libelle: 'Chaque jour' },
  { valeur: 'FREQ=WEEKLY', libelle: 'Chaque semaine' },
  { valeur: 'FREQ=MONTHLY', libelle: 'Chaque mois' },
  { valeur: 'FREQ=YEARLY', libelle: 'Chaque annee' },
] as const;

export function libelleRecurrence(recurrence: string | null): string | null {
  if (!recurrence) return null;
  const trouve = RECURRENCES.find((r) => r.valeur === recurrence);
  if (trouve) return trouve.libelle;
  const freq = /FREQ=([A-Z]+)/.exec(recurrence)?.[1];
  return freq ? 'Repetee (' + freq.toLowerCase() + ')' : 'Repetee';
}

function avancerDate(date: string, freq: string, interval: number): string {
  const [a, m, j] = date.split('-').map(Number);
  // Midi UTC : aucune addition ne peut faire basculer la date d'un jour a
  // cause d'un decalage de fuseau.
  const d = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1, 12));

  if (freq === 'DAILY') d.setUTCDate(d.getUTCDate() + interval);
  else if (freq === 'WEEKLY') d.setUTCDate(d.getUTCDate() + 7 * interval);
  else if (freq === 'YEARLY') d.setUTCFullYear(d.getUTCFullYear() + interval);
  else if (freq === 'MONTHLY') {
    const jour = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + interval);
    // Le 31 janvier repete chaque mois tombe le 28 ou 29 fevrier, pas le
    // 3 mars : on borne au dernier jour du mois vise.
    const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12)).getUTCDate();
    d.setUTCDate(Math.min(jour, dernier));
  } else return date;

  return d.toISOString().slice(0, 10);
}

/**
 * Echeance de la prochaine occurrence, ou null si l'item ne se repete pas.
 *
 * On avance jusqu'a depasser aujourd'hui : une tache quotidienne oubliee
 * pendant cinq jours doit reapparaitre demain, pas cinq fois de suite dans le
 * passe. La borne de 400 iterations couvre un an de quotidien et empeche
 * toute boucle infinie si la regle est mal formee.
 *
 * L'heure est recomposee via le fuseau de la famille : « 8 h 00 chaque jour »
 * reste 8 h 00 de part et d'autre du changement d'heure, ce qu'une simple
 * addition de 24 heures ne donnerait pas.
 */
export function prochaineOccurrence(
  du_le: string | null,
  recurrence: string | null,
  fuseau: string,
  maintenant = new Date(),
): string | null {
  if (!du_le || !recurrence) return null;

  const freq = /FREQ=([A-Z]+)/.exec(recurrence)?.[1];
  if (!freq || !['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(freq)) return null;
  const interval = Math.max(1, Number(/INTERVAL=(\d+)/.exec(recurrence)?.[1] ?? '1'));

  const { date, heure } = champsDepuisEcheance(du_le, fuseau);
  if (!date) return null;

  const aujourdhui = jourLocal(maintenant, fuseau);
  let suivante = avancerDate(date, freq, interval);
  for (let i = 0; i < 400 && suivante <= aujourdhui; i++) {
    suivante = avancerDate(suivante, freq, interval);
  }

  return heure ? isoDepuisDateEtHeure(suivante, heure, fuseau) : suivante;
}
